<template>
  <main class="page-operative page-operative-data-analysis page-operative-data-analysis-equities page-operative-data-analysis-equities-list d-flex flex-column h-100">
    <div class="flex-grow-1 container-fluid d-flex flex-column h-100">
      <div class="d-flex align-items-center justify-content-between flex-wrap flex-md-nowrap mb-5">
        <PageTitle class="mb-2 mb-md-0">Equity</PageTitle>

        <app-router-link v-if="userCanEdit" class="btn btn-primary" :to="{ name: 'operative-company.data-analysis.equity.add' }">
          Aggiungi conferimento equity
        </app-router-link>
      </div>

      <div class="flex-grow-1 d-flex flex-column h-100 overflow-auto">
        <LoadingDataLoader v-if="isLoadingData"></LoadingDataLoader>
        <ErrorOnLoadingDataFeedback v-else-if="errorLoadingData"></ErrorOnLoadingDataFeedback>
        <template v-else>
          <div class="d-flex align-items-center justify-content-between">
            <ForecastListSwitcher :options="forecastListSwitcherOptions" :viewMode="viewMode" @update:viewMode="setViewMode" />
          </div>

          <template v-if="viewMode === 'forecast' && data">
            <div class="overflow-auto report-texts-size">
              <OperativeTable dense sticky>
                <OperativeTableAggregateTable key="aggregate">
                  <OperativeTableBodyHeader :years="aggregateTableHeadings" show-years>{{ aggregateTableRows[0].name }}</OperativeTableBodyHeader>
                  <OperativeTableBodyRow :values="aggregateTableRows[0].data.capitaleSociale">
                    Capitale Sociale
                    <template #cell-content="{ value }">{{ value | money }}</template>
                  </OperativeTableBodyRow>
                  <OperativeTableBodyRow :values="aggregateTableRows[0].data.prestitoSoci">
                    Prestito Soci
                    <template #cell-content="{ value }">{{ value | money }}</template>
                  </OperativeTableBodyRow>
                  <OperativeTableBodyRow :values="aggregateTableRows[0].data.riserveUtili">
                    Riserve di Utili
                    <template #cell-content="{ value }">{{ value | money }}</template>
                  </OperativeTableBodyRow>
                  <OperativeTableBodyRow :values="aggregateTableRows[0].data.riserve">
                    Altre Riserve
                    <template #cell-content="{ value }">{{ value | money }}</template>
                  </OperativeTableBodyRow>
                  <OperativeTableBodyRow :values="aggregateTableRows[0].data.liquidita">
                    Liquidità
                    <template #cell-content="{ value }">{{ value | money }}</template>
                  </OperativeTableBodyRow>
                  <OperativeTableBodyRow :values="aggregateTableRows[0].data.dividendi">
                    Pagamento Dividendi
                    <template #cell-content="{ value, col }">
                      <template v-if="!userCanEdit || !aggregateTableRows[0].data.enabledDividendi[col]">
                        {{ value | money }}
                      </template>
                      <template v-else>
                        <span>
                          {{ value | money }}
                        </span>
                        <button class="btn btn-link text-primary small pr-0 text-right d-inline py-0 pl-1" @click="showAddBudgetModal(aggregateTableRows[0].id, aggregateTableRows[0].name, 'dividendo', 'Dividendi', aggregateTableRows[0].data.month[col], aggregateTableRows[0].data.year[col], value)">
                          <i class="fas fa-pencil-alt"></i>
                        </button>
                      </template>
                    </template>
                  </OperativeTableBodyRow>
                  <OperativeTableBodyRow :values="aggregateTableRows[0].data.rimborsoPrestito">
                    Rimborso Prestito Soci
                    <template #cell-content="{ value, col }">
                      <template v-if="!userCanEdit || !aggregateTableRows[0].data.enabledRimborsoPrestito[col]">
                        {{ value | money }}
                      </template>
                      <template v-else>
                        <span>
                          {{ value | money }}
                        </span>
                        <button class="btn btn-link text-primary small pr-0 text-right d-inline py-0 pl-1" @click="showAddBudgetModal(aggregateTableRows[0].id, aggregateTableRows[0].name, 'rimborsoPrestito', 'Rimborso Prestito Soci', aggregateTableRows[0].data.month[col], aggregateTableRows[0].data.year[col], value)">
                          <i class="fas fa-pencil-alt"></i>
                        </button>
                      </template>
                    </template>
                  </OperativeTableBodyRow>
                </OperativeTableAggregateTable>
              </OperativeTable>

              <BModal :title="currentModalInfo.title" dialog-class="modal--type-dialog" id="edit-value-modal" ref="edit-value-modal" hide-footer centered scrollable>
                <form @submit.prevent="saveCurrentModalInfo">
                  <div class="form-group row align-items-end py-4">
                    <div class="col">
                    <span class="font-weight-bold d-block">
                      {{ currentModalInfo.typeName }}
                    </span>
                      <span class="text-muted small">
                      Mensilità: {{ currentModalInfo.month }}/{{ currentModalInfo.year }}
                    </span>
                    </div>
                    <div class="col">
                      <input type="number" v-model="currentModalInfo.value" class="form-control col" placeholder="Inserisci importo">
                    </div>
                  </div>
                  <div class="text-right">
                    <button class="btn btn-primary" type="submit">Salva</button>
                  </div>
                </form>
              </BModal>
            </div>
          </template>
          <EquitiesListTable v-else-if="viewMode === 'list' && results" class="report-texts-size" :equities="results" :edit-enabled="userIsAdmin || userIsContextCompanyConsultant || userIsContextCompanyOwner" @delete="removeEquity" />
        </template>
      </div>
    </div>
  </main>
</template>

<script>

import operativePageMixin from '../../../../../mixins/operativePage';
import loadingDataMixin from '../../../../../mixins/loadingData';
import computedFinancialDataMixin from '../../../../../mixins/computedFinancialData';
import confirmWithModalMixin from '../../../../../libs/Confirm/mixins/confirmWithModal';
import formatMoneyFilter from '../../../../../filters/formatMoney';
import { BModal } from 'bootstrap-vue';

export default {
  name: 'page-operative-company-data-analysis-equities-list',
  mixins: [operativePageMixin, loadingDataMixin, computedFinancialDataMixin, confirmWithModalMixin],
  components: {
    BModal,
    PageTitle: () => import('../../../../components/Layout/LayoutOperative/PageTitle'),
    ErrorOnLoadingDataFeedback: () => import('../../../../components/Operative/ErrorOnLoadingDataFeedback'),
    LoadingDataLoader: () => import('../../../../components/Operative/LoadingDataLoader'),
    OperativeTable: () => import('../../../../components/Operative/OperativeTable'),
    OperativeTableBodyHeader: () => import('../../../../components/Operative/OperativeTableBodyHeader'),
    OperativeTableBodyRow: () => import('../../../../components/Operative/OperativeTableBodyRow'),
    OperativeTableAggregateTable: () => import('../../../../components/Operative/OperativeTable/OperativeTableAggregateTable'),
    ForecastListSwitcher: () => import('../../../../components/Operative/ForecastListSwitcher'),
    EquitiesListTable: () => import('../../../../components/Operative/ListTable/EquitiesListTable'),
    AppRouterLink: () => import('../../../../components/Layout/AppRouterLink'),
  },
  filters: {
    money: formatMoneyFilter,
  },
  data () {
    return {
      data: null,
      results: null,
      viewMode: 'forecast',
      forecastListSwitcherOptions: [
        { name: 'forecast', label: 'Previsionale' },
        { name: 'list', label: 'Conferimenti' },
      ],
      currentModalInfo: {
        id: null,
        title: null,
        type: null,
        typeName: null,
        month: null,
        year: null,
        value: null,
      },
    };
  },
  computed: {
    aggregateTableHeadings () {
      if (!this.data || this.data.length < 0) {
        return [];
      }

      return this.convertToMonthsHeading(this.data[0].data);
    },
    aggregateTableRows () {
      if (!this.data || this.data.length < 0) {
        return [];
      }

      return this.data.map(d => ({
        id: d.id,
        name: d.name,
        data: this.convertToRowGroup(d.data, [
          'capitaleSociale',
          'riserve',
          'prestitoSoci',
          'liquidita',
          'riserveUtili',
          'dividendi',
          'rimborsoPrestito',
          'disponibilitaDividendi',
          'enabledDividendi',
          'disponibilitaRimborsoPrestito',
          'enabledRimborsoPrestito',
          'year',
          'month',
        ]),
      }));
    },
  },
  methods: {
    setViewMode (mode) {
      if (mode === 'forecast') {
        this.retrieveForecastData();
      } else if (mode === 'list') {
        this.retrieveListData();
      }
      this.viewMode = mode;
    },
    retrieveForecastData () {
      if (!this.company) {
        return;
      }

      this.resetErrorOnLoadingData();
      this.setDataLoading();
      this.$api.getComputedEquities(this.company?.id)
        .then(res => {
          this.data = res?.data?.data || [];
        })
        .catch(this.setErrorOnLoadingData)
        .finally(this.setDataLoaded)
      ;
    },
    retrieveListData () {
      if (!this.company) {
        return;
      }

      this.resetErrorOnLoadingData();
      this.setDataLoading();
      this.$api.getEquities(this.company?.id)
        .then(res => {
          this.results = res?.data?.equities || [];
        })
        .catch(this.setErrorOnLoadingData)
        .finally(this.setDataLoaded)
      ;
    },
    showAddBudgetModal (id, title, type, typeName, month, year, value) {
      this.currentModalInfo.id = id;
      this.currentModalInfo.title = title;
      this.currentModalInfo.type = type;
      this.currentModalInfo.typeName = typeName;
      this.currentModalInfo.month = month;
      this.currentModalInfo.year = year;
      this.currentModalInfo.value = value;
      this.$refs['edit-value-modal'].show();
    },
    saveCurrentModalInfo () {
      this.$api.saveEquityValue(this.company.id, this.currentModalInfo.type, this.currentModalInfo.year, this.currentModalInfo.month, this.currentModalInfo.value)
        .then(() => {
          this.$refs['edit-value-modal'].hide();
          this.setViewMode('forecast');
        })
        .catch(this.$log.errors)
      ;
    },
    removeEquity (id) {
      this.confirm('Cancellare il conferimento equity selezionato? L\'operazione è irreversibile', 'Ok', 'Annulla')
        .then(confirmed => {
          if (!confirmed) {
            return;
          }

          this.$api.deleteEquity(this.company.id, id).then(() => this.retrieveListData()).catch(this.$log.error);
        })
      ;
    },
  },
  watch: {
    company: {
      immediate: true,
      handler: function () {
        this.setViewMode('forecast');
      },
    },
  },
};

</script>
